/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { useLastSubmittedUserParameters } from "app/hooks"
import {
  selectComputedRanges,
  selectInitialScrs,
  selectInitialScrsDataReady,
} from "features/initialScrs/InitialScrsSlice"
import HistoricalDataFilters from "features/results/components/HistoricalDataFilters"
import ResultsTabs, { Table } from "features/results/components/ResultsTabs"
import DataTable from "features/results/DataTable"
import {
  buildInitializeFiltersPayload,
  initializeFilters,
  selectFiltersInitialized,
  selectHistoricalDataFilters,
} from "features/results/HistoricalDataFiltersSlice"
import {
  selectResultsRecalculationFailed,
  selectUiTables,
  selectUiTablesCalculationPending,
  setResultsFresh,
} from "features/results/ResultsSlice"
import { useCoalescedRecalculation } from "features/results/useCoalescedRecalculation"
import { useTableRenderTracking } from "features/results/useTableRenderTracking"
import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

// The two timing constants were part of this module's exported surface before the
// scheduling moved to recalculationScheduler.ts, and the coalescing examples in
// Results.test.tsx assert against them from here. Re-exported rather than duplicated:
// there is still exactly one definition, in the module that acts on them.
export {
  RECALCULATION_DEBOUNCE_MS,
  RECALCULATION_MAX_WAIT_MS,
} from "features/results/recalculationScheduler"

const Results = () => {
  const { uiTables, fresh: areUiTablesFresh } = useSelector(selectUiTables)
  const uiTablesCalculationPending = useSelector(selectUiTablesCalculationPending)
  const recalculationFailed = useSelector(selectResultsRecalculationFailed)
  const { fresh: areInitialScrsFresh, data: initialScrsData } = useSelector(selectInitialScrs)
  const computedRanges = useSelector(selectComputedRanges)
  const initialScrsDataReady = useSelector(selectInitialScrsDataReady)
  const filtersInitialized = useSelector(selectFiltersInitialized)
  const userParameters = useLastSubmittedUserParameters()
  const historicalDataFilters = useSelector(selectHistoricalDataFilters)
  const dispatch = useDispatch()

  // Initialize filters based on computed ranges when data becomes ready
  useEffect(() => {
    const initializeFiltersPayload = buildInitializeFiltersPayload(computedRanges)
    if (initialScrsDataReady && initializeFiltersPayload && !filtersInitialized) {
      dispatch(initializeFilters(initializeFiltersPayload))
      dispatch(setResultsFresh(false)) // Mark tables as stale instead of setting calculationPending
    }
  }, [initialScrsDataReady, computedRanges, filtersInitialized, dispatch])

  // Recalculate tables when necessary. Filter drags arrive at rc-slider's per-step
  // frequency, so the scheduling - coalescing, superseded-data cancellation and the
  // unmount flush - lives in useCoalescedRecalculation.
  useCoalescedRecalculation({
    userParameters,
    historicalDataFilters,
    initialScrsData,
    initialScrsDataReady,
    filtersInitialized,
    uiTablesCalculationPending,
    // AIDEV-NOTE: (#1251) The same flag the banner below renders from. The scheduler reads
    // it to leave an already-failed selection alone on arrival, rather than replaying the
    // failure on the route that renders SentryErrorBoundary instead of that banner.
    recalculationFailed,
  })

  const consumptionAmountBasedTables: readonly Table[] = uiTables?.maybeUserConsumptionAmountResults
    ? [
        {
          id: "user-cape-failure-rates",
          caption: "CAPE-based failure rates for your specified consumption amount",
          data: uiTables.maybeUserConsumptionAmountResults.capeFailureRates,
          category: "failure",
        },
        {
          id: "user-drawdown-failure-rates",
          caption: "Market drawdown-based failure rates for your specified consumption amount",
          data: uiTables.maybeUserConsumptionAmountResults.drawdownFailureRates,
          category: "failure",
        },
      ]
    : []

  const supplementaryTables: readonly Table[] = uiTables
    ? [
        {
          id: "cape-consumption-amounts",
          caption: "Initial consumption amounts (p.a.) to target different failure rates",
          data: uiTables.capeConsumptionAmounts,
          category: "consumption",
        },
        {
          id: "drawdown-consumption-amounts",
          caption: "Initial consumption amounts (p.a.) conditional on S&P 500 drawdown",
          data: uiTables.drawdownConsumptionAmounts,
          category: "consumption",
        },
        {
          id: "cape-consumption-rates",
          caption: "Initial consumption rates (p.a.) to target different failure rates",
          data: uiTables.capeConsumptionRates,
          category: "rates",
        },
        {
          id: "drawdown-consumption-rates",
          caption:
            "Initial consumption rates (p.a.) to target different failure rates, conditional on S&P 500 drawdown",
          data: uiTables.drawdownConsumptionRates,
          category: "rates",
        },
        {
          id: "cape-failure-rates",
          caption: "Failure rates of different initial consumption rates (p.a.)",
          data: uiTables.capeFailureRates,
          category: "failure",
        },
        {
          id: "drawdown-failure-rates",
          caption:
            "Failure rates conditional on S&P 500 drawdown of different initial consumption rates (p.a.)",
          data: uiTables.drawdownFailureRates,
          category: "failure",
        },
      ]
    : []

  const allTables: readonly Table[] = [...consumptionAmountBasedTables, ...supplementaryTables]
  const totalTableCount = allTables.length

  const { createOnRendered } = useTableRenderTracking(uiTables, totalTableCount, () => {
    dispatch(setResultsFresh(true))
  })

  // Group tables by category (if results exist)
  const failureRatesTables = allTables.filter(table => table.category === "failure")
  const consumptionAmountTables = allTables.filter(table => table.category === "consumption")
  const consumptionRatesTables = allTables.filter(table => table.category === "rates")

  // AIDEV-NOTE: A user cohort matching no retirements is dropped from every
  // table rather than claiming 0% failure over no data (issue #532). Without
  // this notice that removal is silent: the only other cue is the retirement
  // count in the filters panel, which sits above the tabs and reads as unrelated.
  const userCohortIsEmpty = uiTables != null && uiTables.userFiltersRetirementCount === 0

  // Helper function to render a group of tables
  const renderTableGroup = (tables: typeof allTables) => {
    return (
      <>
        {userCohortIsEmpty && (
          <div className="alert alert-warning" role="alert">
            Your filters match no historical retirements, so the &quot;Your Filters&quot; column is
            not shown.
          </div>
        )}
        {tables.map(table => (
          <DataTable
            key={table.id}
            caption={table.caption}
            tableData={table.data}
            onRendered={createOnRendered()}
          />
        ))}
      </>
    )
  }

  return (
    <>
      {/* AIDEV-NOTE: (#1247 review) The message deliberately does not offer Calculate
          Results. submitForm early-returns whenever the parameters equal the last
          submitted ones (UserParametersForm.tsx, the `_.isEqual(userParameters,
          lastSubmittedParameters)` guard), which is exactly this state - the filter path
          never touches parameters - so the click would silently do nothing and the app
          would read as frozen. Adjusting the selection is the recovery that works.

          AIDEV-NOTE: (#1069) The tables below are still the previous selection's, and
          without this the only cue was a toast that clears itself after five seconds.
          Dimming them alongside the notice is the same treatment the stale-data path
          below already uses, for the same reason: the figures are readable but must
          not read as current. */}
      {recalculationFailed && (
        <div id="recalculation-failed-message" className="alert alert-danger" role="alert">
          These results could not be updated for your current Historical Data Selection, so the
          tables below still show the previous selection. Adjust the selection to try again.
        </div>
      )}

      {!areInitialScrsFresh && (
        <div id="stale-message" className="alert alert-info" role="alert">
          Please click Calculate Results to update.
        </div>
      )}

      <div className={areInitialScrsFresh ? "" : "opacity-50"}>
        <HistoricalDataFilters />
        {/* The failure dimming stops at the tables on purpose: the filters above are how
            the user recovers from it, so they stay at full strength. */}
        <div className={recalculationFailed ? "opacity-50" : ""}>
          <ResultsTabs
            failureRatesTables={failureRatesTables}
            consumptionAmountTables={consumptionAmountTables}
            consumptionRatesTables={consumptionRatesTables}
            areResultsFresh={areUiTablesFresh}
            renderTableGroup={renderTableGroup}
          />
        </div>
      </div>
    </>
  )
}

export default Results
